import React, { useState } from 'react';
import {
  dayjsFromApiUtc,
  formatDateFromDayjsInTzWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
  stringYMDFromDate,
  shortDateFormat,
} from '@utils/dateWithTz.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';

import SvgCalendar from '@icons/calendar.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import { Timeslot } from '@app/pages/carrier/demand-detail/CarrierQuote.tsx';
import dayjs from 'dayjs';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import { InputMask } from 'primereact/inputmask';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { ValidationError } from 'yup';
import { findValidationMessage } from '@utils/validation.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface TimeslotProps {
  timeslot: Timeslot;
  index: number;
  disableCarrierQuoteTimeslots?: boolean;
  onChange: (locationId: string, updatedTimeslot: Timeslot) => void;
  validationErrors: ValidationError[];
}

const TimeslotItem: React.FC<TimeslotProps> = ({ timeslot, index, disableCarrierQuoteTimeslots, onChange, validationErrors }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();

  const [proposalEnabled, setProposalEnabled] = useState<boolean>(!timeslot.useCustomerSlot);

  console.log(timeslot)

  return (
    <>
      <div className="flex flex-col">
        <div
          className="font-dm-sans text-[18px] font-normal leading-normal text-content-primary"
        >
          {`${index + 1}. ${timeslot.locationName}, ${timeslot.streetNr} ${timeslot.zipCode} ${timeslot.city}`}
          <COTimezone timezoneId={timeslot.customerTimeslots[0]?.timezoneId}/>
        </div>
        <COHeadline
          variant={'h4'}
          title={tr('carrier.demand.quote.timeslots.expected.title', 'Expected time slots for this location')}
          icon={<SvgCalendar />}
          className="pt-4"
        />
        <div className="flex flex-row flex-wrap gap-2">
          <span className="font-dm-sans text-[14px] font-normal leading-normal text-content-primary">
            {timeslot.customerTimeslots.map((slot, idx, arr) => {
              const timezoneId = slot?.timezoneId || 'Europe/Prague';
              return (
                <span key={idx}>
                  {formatDateFromDayjsInTzWithLocale(dayjsFromApiUtc(slot.utcStart), timezoneId)}
                  <span className="mx-2 text-content-tertiary">|</span>
                  {formatTimeIntervalFromDayjsIntervalWithLocale(dayjsFromApiUtc(slot.utcStart), dayjsFromApiUtc(slot.utcEnd), timezoneId)}
                  {idx < arr.length - 1 && <span>, </span>}
                </span>
              );
            })}
          </span>
        </div>
        {proposalEnabled && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-row justify-between pb-2">
              <COHeadline
                variant={'h4'}
                title={tr('carrier.demand.quote.timeslots.proposed.title', 'Your proposal')}
                icon={<SvgCalendar />}
                className="pt-4"
              />
            </div>
            <div className="flex flex-row w-full gap-2 items-start justify-between">
              <Field
                required={!timeslot.carrierTimeslot?.day}
                width={'100%'}
                errorMessage={findValidationMessage(validationErrors, `timeslots[${index}].carrierTimeslot.day`, tr)}
              >
                <Calendar
                  locale={i18n.language}
                  selectionMode={'single'}
                  value={timeslot.carrierTimeslot?.day ? new Date(`${timeslot.carrierTimeslot.day}T00:00:00`) : null}
                  onChange={e =>
                    onChange(
                      timeslot.locationId,
                      {
                        ...timeslot,
                        carrierTimeslot: {
                          ...timeslot.carrierTimeslot,
                          day: e.value && e.value instanceof Date ? stringYMDFromDate(e.value) : null,
                        },
                      },
                    )
                  }
                  showIcon
                  dateFormat={shortDateFormat}
                  readOnlyInput
                  showButtonBar={true}
                  showWeek={true}
                  className="!w-full"
                />
              </Field>
              <RingilButton
                mode={'table'}
                icon={<SvgDelete />}
                onClick={() => {
                  onChange(timeslot.locationId, {
                    ...timeslot,
                    useCustomerSlot: true,
                    carrierTimeslot: null,
                  });
                  setProposalEnabled(false);
                }}
              />
            </div>
            <div className="flex flex-row gap-2 items-start">
              <Field
                disabled={timeslot.carrierTimeslot?.isWholeDay}
                required={!timeslot.carrierTimeslot?.isWholeDay && !timeslot.carrierTimeslot?.timeFrom}
                width={'100%'}
                errorMessage={findValidationMessage(validationErrors, `timeslots[${index}].carrierTimeslot.timeFrom`, tr)}
              >
                <InputMask
                  mask={'99:99'}
                  value={timeslot.carrierTimeslot?.timeFrom && !timeslot.carrierTimeslot?.isWholeDay ? timeslot.carrierTimeslot.timeFrom : ''}
                  onComplete={e =>
                    onChange(
                      timeslot.locationId,
                      {
                        ...timeslot,
                        carrierTimeslot: {
                          ...timeslot.carrierTimeslot,
                          timeFrom: e.value,
                        },
                      },
                    )
                  }
                  disabled={timeslot.carrierTimeslot?.isWholeDay}
                  className="h-9 w-full"
                />
              </Field>
              <Field
                disabled={timeslot.carrierTimeslot?.isWholeDay}
                required={true}
                width={'100%'}
                errorMessage={findValidationMessage(validationErrors, `timeslots[${index}].carrierTimeslot.timeTo`, tr)}
              >
                <InputMask
                  mask={'99:99'}
                  value={timeslot.carrierTimeslot?.timeTo && !timeslot.carrierTimeslot?.isWholeDay ? timeslot.carrierTimeslot.timeTo : ''}
                  onComplete={e =>
                    onChange(
                      timeslot.locationId,
                      {
                        ...timeslot,
                        carrierTimeslot: {
                          ...timeslot.carrierTimeslot,
                          timeTo: e.value,
                        },
                      },
                    )
                  }
                  disabled={timeslot.carrierTimeslot?.isWholeDay}
                  className="h-9 w-full"
                />
              </Field>
              <RingilInputSwitch
                label={tr('carrier.demand.quote.timeslots.proposed.wholeDay.label', 'Whole day')}
                value={timeslot.carrierTimeslot?.isWholeDay || false}
                onChange={value =>
                  onChange(
                    timeslot.locationId,
                    {
                      ...timeslot,
                      carrierTimeslot: {
                        ...timeslot.carrierTimeslot,
                        timeFrom: value ? '00:00' : '',
                        timeTo: value ? '23:59' : '',
                        isWholeDay: value,
                      },
                    },
                  )
                }
                className="!w-full pt-1"
              />
            </div>

          </div>
        )}
        {!proposalEnabled && !disableCarrierQuoteTimeslots && (
          <RingilButton
            mode={'text-only'}
            label={tr('carrier.demand.quote.button.timeslot.proposeNew.label', 'Propose new timeslot')}
            onClick={() => {
              if (!timeslot.carrierTimeslot) {
                onChange(timeslot.locationId, {
                  ...timeslot,
                  useCustomerSlot: false,
                  carrierTimeslot: {
                    day: dayjs().utc().startOf('day').format('YYYY-MM-DD'),
                    isWholeDay: false,
                  },
                });
              }
              setProposalEnabled(true);
            }}
            className="!px-0"
          />
        )}
      </div>
    </>

  );
};

export default TimeslotItem;